import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api, { resolveAsset } from "../lib/api";
import { useAuth } from "../lib/auth";
import { BRAND } from "../lib/brand";
import { parseMedia } from "../lib/media";
import MediaItem from "../components/MediaItem";
import {
  LogOut, Trash2, Plus, Image as ImageIcon, MessageSquareQuote, Calendar, Megaphone, Inbox,
  Upload, Star, Edit3, Save, X, ChevronUp, ChevronDown, CalendarCheck, Youtube, Facebook, CloudUpload, CheckCircle2,
} from "lucide-react";

const TABS = [
  { id: "gallery", label: "Gallery", icon: ImageIcon },
  { id: "testimonials", label: "Testimonials", icon: MessageSquareQuote },
  { id: "calendar", label: "Calendar", icon: Calendar },
  { id: "announcements", label: "Announcements", icon: Megaphone },
  { id: "submissions", label: "Submissions", icon: Inbox },
  { id: "visits", label: "School Visits", icon: CalendarCheck },
];

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("gallery");

  return (
    <main className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="bg-white border-b border-[#eee] sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-home-link">
            <img src={BRAND.logo} className="w-9 h-9" alt="logo" />
            <div>
              <div className="font-heading font-bold text-brand-blue text-sm">Innovative Preschool</div>
              <div className="text-xs text-[color:var(--text-secondary)]">Admin Console</div>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <div className="text-sm hidden sm:block">
              <span className="text-[color:var(--text-secondary)]">Signed in as </span>
              <span className="font-semibold">{user?.email}</span>
            </div>
            <button onClick={logout} className="btn-ghost !py-1.5 !px-4 !text-sm" data-testid="admin-logout">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-3">
          <nav className="card-soft bg-white p-3 lg:sticky lg:top-24" aria-label="Admin sections">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-semibold mb-1 transition ${tab === t.id ? "bg-brand-blue text-white" : "text-[color:var(--text-primary)] hover:bg-light-blue"}`}
                data-testid={`admin-tab-${t.id}`}
              >
                <t.icon size={16} /> {t.label}
              </button>
            ))}
          </nav>
        </aside>

        <section className="lg:col-span-9 space-y-6">
          {tab === "gallery" && <GalleryManager />}
          {tab === "testimonials" && <TestimonialsManager />}
          {tab === "calendar" && <CalendarManager />}
          {tab === "announcements" && <AnnouncementsManager />}
          {tab === "submissions" && <SubmissionsManager />}
          {tab === "visits" && <VisitsManager />}
        </section>
      </div>
    </main>
  );
};

const PanelCard = ({ title, action, children }) => (
  <div className="card-soft bg-white p-6 sm:p-8">
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
      <h2 className="font-heading text-xl font-bold">{title}</h2>
      {action}
    </div>
    {children}
  </div>
);

// =================== GALLERY ===================
const GalleryManager = () => {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [editUrl, setEditUrl] = useState("");
  const [editCaption, setEditCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => setItems((await api.get("/gallery")).data);
  useEffect(() => { load(); }, []);

  const upload = async (file, target) => {
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try {
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (target === "edit") setEditUrl(r.data.url); else setUrl(r.data.url);
    } catch (e) { alert("Upload failed: " + (e?.response?.data?.detail || "error")); }
    finally { setUploading(false); }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!url) return;
    await api.post("/gallery", { image_url: url, caption });
    setUrl(""); setCaption(""); setAdding(false); load();
  };

  const startEdit = (g) => {
    setEditId(g.id); setEditUrl(g.image_url); setEditCaption(g.caption || "");
  };
  const saveEdit = async () => {
    await api.patch(`/gallery/${editId}`, { image_url: editUrl, caption: editCaption });
    setEditId(null); load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    await api.delete(`/gallery/${id}`); load();
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
    await api.post("/gallery/reorder", { ids: next.map((x) => x.id) });
  };

  const [rehostingId, setRehostingId] = useState(null);
  const rehost = async (id) => {
    setRehostingId(id);
    try {
      await api.post(`/gallery/${id}/rehost`);
      await load();
    } catch (e) {
      alert("Re-host failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setRehostingId(null);
    }
  };

  const canRehost = (url) => {
    if (!url) return false;
    if (url.includes("supabase.co/storage/v1/object/public/photos/")) return false;
    // Skip iframe embeds (YouTube / Facebook reel/post)
    return !/youtube\.com|youtu\.be|facebook\.com\/(?:reel|photo|posts|watch|videos|video\.php)/i.test(url);
  };

  return (
    <PanelCard
      title="Gallery photos"
      action={
        <button onClick={() => setAdding((v) => !v)} className="btn-primary !py-2 !px-4 !text-sm" data-testid="admin-add-gallery-btn">
          <Plus size={16} /> {adding ? "Cancel" : "Add photo"}
        </button>
      }
    >
      {adding && (
        <form onSubmit={add} className="mb-6 bg-cream rounded-2xl p-5 space-y-3" data-testid="admin-gallery-form">
          <div className="flex flex-col sm:flex-row gap-3">
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Paste image URL, YouTube link, or Facebook reel/post URL" className="flex-1 bg-white border border-[#e6e2d8] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-brand-blue" data-testid="admin-gallery-url" />
            <input ref={fileRef} type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], "add")} className="hidden" data-testid="admin-gallery-file" />
            <button type="button" onClick={() => fileRef.current?.click()} className="btn-ghost !py-2 !px-4 !text-sm" data-testid="admin-gallery-upload-btn">
              <Upload size={14} /> {uploading ? "Uploading..." : "Upload image"}
            </button>
          </div>
          <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption (optional)" className="w-full bg-white border border-[#e6e2d8] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-brand-blue" data-testid="admin-gallery-caption" />
          {url && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-[color:var(--text-secondary)]">
                <MediaTypeBadge url={url} /> <span>Preview will appear on the gallery as shown below.</span>
              </div>
              <div className="max-w-xs rounded-xl overflow-hidden bg-white">
                <MediaItem url={url} aspectClassName={getPreviewAspect(url)} testId="admin-gallery-preview" />
              </div>
            </div>
          )}
          <button type="submit" disabled={!url} className="btn-primary !py-2 !px-4 !text-sm" data-testid="admin-gallery-save">Save item</button>
        </form>
      )}

      <p className="text-xs text-[color:var(--text-secondary)] mb-3">Tip — paste any <strong>image URL</strong>, <strong>YouTube</strong> link, or <strong>Facebook</strong> reel/video/post URL. Use up/down arrows to change display order.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((g, i) => (
          <div key={g.id} className="relative group rounded-2xl overflow-hidden bg-cream" data-testid={`admin-gallery-item-${i}`}>
            {editId === g.id ? (
              <div className="p-3 space-y-2">
                <div className="rounded-lg overflow-hidden bg-white">
                  <MediaItem url={editUrl} aspectClassName="aspect-video" />
                </div>
                <input value={editUrl} onChange={(e) => setEditUrl(e.target.value)} className="w-full bg-white border border-[#e6e2d8] rounded-lg px-2 py-1 text-xs" placeholder="Image URL, YouTube or Facebook" data-testid={`admin-gallery-edit-url-${i}`} />
                <input value={editCaption} onChange={(e) => setEditCaption(e.target.value)} placeholder="Caption" className="w-full bg-white border border-[#e6e2d8] rounded-lg px-2 py-1 text-xs" data-testid={`admin-gallery-edit-caption-${i}`} />
                <div className="flex gap-1">
                  <button onClick={saveEdit} className="flex-1 bg-brand-blue text-white text-xs rounded-lg py-1 font-bold" data-testid={`admin-gallery-edit-save-${i}`}><Save size={12} className="inline" /> Save</button>
                  <button onClick={() => setEditId(null)} className="flex-1 bg-cream border text-xs rounded-lg py-1 font-bold" data-testid={`admin-gallery-edit-cancel-${i}`}>Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <MediaItem url={g.image_url} caption={g.caption} aspectClassName="aspect-square" cacheBust={g.created_at} />
                <div className="absolute top-2 left-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                  <button onClick={() => move(i, -1)} className="w-7 h-7 rounded-full bg-white/95 flex items-center justify-center" title="Move up" data-testid={`admin-gallery-up-${i}`}><ChevronUp size={14} /></button>
                  <button onClick={() => move(i, 1)} className="w-7 h-7 rounded-full bg-white/95 flex items-center justify-center" title="Move down" data-testid={`admin-gallery-down-${i}`}><ChevronDown size={14} /></button>
                </div>
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                  {canRehost(g.image_url) && (
                    <button
                      onClick={() => rehost(g.id)}
                      disabled={rehostingId === g.id}
                      className="w-8 h-8 rounded-full bg-white/95 text-brand-blue flex items-center justify-center disabled:opacity-60"
                      title="Move image to Supabase Storage (permanent)"
                      data-testid={`admin-gallery-rehost-${i}`}
                    >
                      {rehostingId === g.id ? <span className="w-3 h-3 rounded-full border-2 border-brand-blue border-t-transparent animate-spin" /> : <CloudUpload size={14} />}
                    </button>
                  )}
                  {g.image_url && g.image_url.includes("supabase.co/storage/v1/object/public/photos/") && (
                    <div
                      className="w-8 h-8 rounded-full bg-light-green text-[#1d6b32] flex items-center justify-center pointer-events-none"
                      title="Stored on Supabase — permanent"
                      data-testid={`admin-gallery-permanent-${i}`}
                    >
                      <CheckCircle2 size={14} />
                    </div>
                  )}
                  <button onClick={() => startEdit(g)} className="w-8 h-8 rounded-full bg-white/95 text-brand-blue flex items-center justify-center" title="Edit" data-testid={`admin-gallery-edit-${i}`}><Edit3 size={14} /></button>
                  <button onClick={() => del(g.id)} className="w-8 h-8 rounded-full bg-white/95 text-red-600 flex items-center justify-center" title="Delete" data-testid={`admin-gallery-delete-${i}`}><Trash2 size={14} /></button>
                </div>
              </>
            )}
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-[color:var(--text-secondary)] col-span-full text-center py-10">No items yet — add your first.</div>}
      </div>
    </PanelCard>
  );
};

const MediaTypeBadge = ({ url }) => {
  const m = parseMedia(url);
  const config = {
    image: { Icon: ImageIcon, color: "bg-light-blue text-brand-blue", label: "Image" },
    youtube: { Icon: Youtube, color: "bg-red-100 text-red-700", label: "YouTube" },
    facebook: { Icon: Facebook, color: "bg-blue-100 text-[#1877F2]", label: m.label || "Facebook" },
  }[m.type] || { Icon: ImageIcon, color: "bg-light-blue text-brand-blue", label: "Image" };
  const { Icon, color, label } = config;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[0.7rem] font-bold ${color}`}>
      <Icon size={12} /> {label}
    </span>
  );
};

const getPreviewAspect = (url) => {
  const m = parseMedia(url);
  if (m.type === "facebook" && m.aspect === "9/16") return "aspect-[9/16]";
  if (m.type === "youtube" || m.type === "facebook") return "aspect-video";
  return "aspect-square";
};

// =================== TESTIMONIALS ===================
const TestimonialsManager = () => {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const blank = { name: "", role: "Parent", message: "", rating: 5, photo_url: "", active: true };
  const [form, setForm] = useState(blank);
  const [editForm, setEditForm] = useState(blank);

  const load = async () => setItems((await api.get("/testimonials")).data);
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    await api.post("/testimonials", form);
    setForm(blank); setAdding(false); load();
  };

  const startEdit = (t) => {
    setEditId(t.id);
    setEditForm({ name: t.name, role: t.role, message: t.message, rating: t.rating, photo_url: t.photo_url || "", active: t.active });
  };
  const saveEdit = async () => {
    await api.patch(`/testimonials/${editId}`, editForm);
    setEditId(null); load();
  };

  const del = async (id) => { if (window.confirm("Delete this testimonial?")) { await api.delete(`/testimonials/${id}`); load(); } };

  return (
    <PanelCard
      title="Testimonials"
      action={<button onClick={() => setAdding((v) => !v)} className="btn-primary !py-2 !px-4 !text-sm" data-testid="admin-add-testimonial-btn"><Plus size={16} /> {adding ? "Cancel" : "Add"}</button>}
    >
      {adding && (
        <TestimonialForm value={form} setValue={setForm} onSubmit={add} testIdPrefix="admin-testimonial" />
      )}

      <div className="space-y-3">
        {items.map((t, i) => (
          <div key={t.id} className="bg-cream rounded-2xl p-4" data-testid={`admin-testimonial-item-${i}`}>
            {editId === t.id ? (
              <div className="space-y-3">
                <TestimonialForm value={editForm} setValue={setEditForm} onSubmit={(e) => { e.preventDefault(); saveEdit(); }} testIdPrefix={`admin-testimonial-edit-${i}`} submitLabel="Save changes" />
                <button onClick={() => setEditId(null)} className="text-xs text-[color:var(--text-secondary)] underline" data-testid={`admin-testimonial-edit-cancel-${i}`}>Cancel edit</button>
              </div>
            ) : (
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="font-heading font-bold">{t.name}</div>
                    <span className="text-xs text-[color:var(--text-secondary)]">— {t.role}</span>
                    {!t.active && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">hidden</span>}
                    <span className="flex ml-auto">{Array.from({ length: t.rating }).map((_, k) => <Star key={k} size={12} className="fill-brand-yellow text-brand-yellow" />)}</span>
                  </div>
                  <p className="text-sm text-[color:var(--text-secondary)]">{t.message}</p>
                </div>
                <button onClick={() => startEdit(t)} className="text-brand-blue hover:bg-light-blue p-2 rounded-full" data-testid={`admin-testimonial-edit-${i}`}><Edit3 size={16} /></button>
                <button onClick={() => del(t.id)} className="text-red-600 hover:bg-red-50 p-2 rounded-full" data-testid={`admin-testimonial-delete-${i}`}><Trash2 size={16} /></button>
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-[color:var(--text-secondary)] text-center py-10">No testimonials yet.</div>}
      </div>
    </PanelCard>
  );
};

const TestimonialForm = ({ value, setValue, onSubmit, testIdPrefix, submitLabel = "Save" }) => (
  <form onSubmit={onSubmit} className="mb-2 bg-white rounded-2xl p-5 space-y-3 border border-[#eee]" data-testid={`${testIdPrefix}-form`}>
    <div className="grid sm:grid-cols-2 gap-3">
      <input required placeholder="Parent name" value={value.name} onChange={(e) => setValue({ ...value, name: e.target.value })} className="bg-cream border border-[#e6e2d8] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-brand-blue" data-testid={`${testIdPrefix}-name`} />
      <input placeholder="Role e.g. Parent of Aaradhya" value={value.role} onChange={(e) => setValue({ ...value, role: e.target.value })} className="bg-cream border border-[#e6e2d8] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-brand-blue" data-testid={`${testIdPrefix}-role`} />
    </div>
    <textarea required placeholder="Message" value={value.message} onChange={(e) => setValue({ ...value, message: e.target.value })} className="w-full bg-cream border border-[#e6e2d8] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-brand-blue" rows={3} data-testid={`${testIdPrefix}-message`} />
    <div className="flex items-center gap-3 flex-wrap">
      <label className="text-sm">Rating
        <select value={value.rating} onChange={(e) => setValue({ ...value, rating: Number(e.target.value) })} className="ml-2 border border-[#e6e2d8] rounded-lg px-2 py-1 text-sm" data-testid={`${testIdPrefix}-rating`}>
          {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </label>
      <label className="text-sm flex items-center gap-2">
        <input type="checkbox" checked={value.active} onChange={(e) => setValue({ ...value, active: e.target.checked })} data-testid={`${testIdPrefix}-active`} /> Active
      </label>
    </div>
    <button type="submit" className="btn-primary !py-2 !px-4 !text-sm" data-testid={`${testIdPrefix}-save`}>{submitLabel}</button>
  </form>
);

// =================== CALENDAR ===================
const CalendarManager = () => {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const blank = { title: "", date: "", description: "", event_type: "event" };
  const [form, setForm] = useState(blank);
  const [editForm, setEditForm] = useState(blank);

  const load = async () => setItems((await api.get("/calendar")).data);
  useEffect(() => { load(); }, []);

  const add = async (e) => { e.preventDefault(); await api.post("/calendar", form); setForm(blank); setAdding(false); load(); };
  const startEdit = (it) => { setEditId(it.id); setEditForm({ title: it.title, date: it.date, description: it.description || "", event_type: it.event_type }); };
  const saveEdit = async () => { await api.patch(`/calendar/${editId}`, editForm); setEditId(null); load(); };
  const del = async (id) => { if (window.confirm("Delete this event?")) { await api.delete(`/calendar/${id}`); load(); } };

  return (
    <>
      <CalendarPosterManager />
      <PanelCard
        title="Academic calendar"
        action={<button onClick={() => setAdding((v) => !v)} className="btn-primary !py-2 !px-4 !text-sm" data-testid="admin-add-event-btn"><Plus size={16} /> {adding ? "Cancel" : "Add event"}</button>}
      >
      {adding && <EventForm value={form} setValue={setForm} onSubmit={add} testIdPrefix="admin-event" />}

      <div className="space-y-3">
        {items.map((e, i) => (
          <div key={e.id} className="bg-cream rounded-2xl p-4" data-testid={`admin-event-item-${i}`}>
            {editId === e.id ? (
              <div className="space-y-2">
                <EventForm value={editForm} setValue={setEditForm} onSubmit={(ev) => { ev.preventDefault(); saveEdit(); }} testIdPrefix={`admin-event-edit-${i}`} submitLabel="Save changes" />
                <button onClick={() => setEditId(null)} className="text-xs text-[color:var(--text-secondary)] underline" data-testid={`admin-event-edit-cancel-${i}`}>Cancel edit</button>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-brand-blue text-white flex flex-col items-center justify-center flex-shrink-0">
                  <div className="text-[0.65rem] uppercase">{new Date(e.date).toLocaleString("en", { month: "short" })}</div>
                  <div className="font-heading text-lg font-bold leading-none">{new Date(e.date).getDate()}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-heading font-bold">{e.title}</div>
                    <span className="text-xs bg-light-blue text-brand-blue px-2 py-0.5 rounded-full capitalize">{e.event_type}</span>
                  </div>
                  {e.description && <p className="text-sm text-[color:var(--text-secondary)] mt-1">{e.description}</p>}
                </div>
                <button onClick={() => startEdit(e)} className="text-brand-blue hover:bg-light-blue p-2 rounded-full" data-testid={`admin-event-edit-${i}`}><Edit3 size={16} /></button>
                <button onClick={() => del(e.id)} className="text-red-600 hover:bg-red-50 p-2 rounded-full" data-testid={`admin-event-delete-${i}`}><Trash2 size={16} /></button>
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-[color:var(--text-secondary)] text-center py-10">No events yet.</div>}
      </div>
      </PanelCard>
    </>
  );
};

// =================== CALENDAR POSTER (annual printable image) ===================
const CalendarPosterManager = () => {
  const [posters, setPosters] = useState([]);
  const [adding, setAdding] = useState(false);
  const blank = { image_url: "", label: "", year: "", active: true };
  const [form, setForm] = useState(blank);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => setPosters((await api.get("/calendar-posters")).data);
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try {
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, image_url: r.data.url }));
    } catch (e) { alert("Upload failed: " + (e?.response?.data?.detail || "error")); }
    finally { setUploading(false); }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!form.image_url || !form.label) return;
    // If new poster is active, deactivate previous active ones first
    if (form.active) {
      for (const p of posters.filter((x) => x.active)) {
        await api.patch(`/calendar-posters/${p.id}`, { active: false });
      }
    }
    await api.post("/calendar-posters", form);
    setForm(blank); setAdding(false); load();
  };

  const toggleActive = async (p) => {
    if (!p.active) {
      // Deactivate all other active ones first
      for (const o of posters.filter((x) => x.active && x.id !== p.id)) {
        await api.patch(`/calendar-posters/${o.id}`, { active: false });
      }
    }
    await api.patch(`/calendar-posters/${p.id}`, { active: !p.active });
    load();
  };

  const del = async (id) => { if (window.confirm("Delete this calendar image?")) { await api.delete(`/calendar-posters/${id}`); load(); } };

  return (
    <PanelCard
      title="Annual calendar image"
      action={<button onClick={() => setAdding((v) => !v)} className="btn-primary !py-2 !px-4 !text-sm" data-testid="admin-add-poster-btn"><Plus size={16} /> {adding ? "Cancel" : "Add new"}</button>}
    >
      <p className="text-xs text-[color:var(--text-secondary)] mb-4">
        Upload the printable annual calendar image (BS year). Parents can view and download it from the Academic Calendar page. Only one image can be active at a time — the active one is shown to parents.
      </p>

      {adding && (
        <form onSubmit={add} className="mb-6 bg-cream rounded-2xl p-5 space-y-3" data-testid="admin-poster-form">
          <div className="flex flex-col sm:flex-row gap-3">
            <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="Image URL (or upload below)" className="flex-1 bg-white border border-[#e6e2d8] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-brand-blue" data-testid="admin-poster-url" />
            <input ref={fileRef} type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} className="hidden" data-testid="admin-poster-file" />
            <button type="button" onClick={() => fileRef.current?.click()} className="btn-ghost !py-2 !px-4 !text-sm" data-testid="admin-poster-upload-btn">
              <Upload size={14} /> {uploading ? "Uploading..." : "Upload image"}
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Label e.g. School Calendar 2083 BS" className="bg-white border border-[#e6e2d8] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-brand-blue" data-testid="admin-poster-label" />
            <input value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="Year e.g. 2083 BS (2026/27)" className="bg-white border border-[#e6e2d8] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-brand-blue" data-testid="admin-poster-year" />
          </div>
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="admin-poster-active" />
            Show this calendar on the public page (deactivates the previous one)
          </label>
          {form.image_url && (
            <img src={resolveAsset(form.image_url)} alt="" className="max-h-56 rounded-xl border border-[#eee]" />
          )}
          <button type="submit" disabled={!form.image_url || !form.label} className="btn-primary !py-2 !px-4 !text-sm" data-testid="admin-poster-save">Save calendar</button>
        </form>
      )}

      <div className="space-y-3">
        {posters.map((p, i) => (
          <div key={p.id} className="bg-cream rounded-2xl p-4 flex items-start gap-4" data-testid={`admin-poster-item-${i}`}>
            <img src={resolveAsset(p.image_url)} alt="" className="w-20 h-20 rounded-xl object-cover flex-shrink-0 border border-[#eee] bg-white" />
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold">{p.label}</div>
              {p.year && <div className="text-xs text-[color:var(--text-secondary)]">{p.year}</div>}
              <button
                onClick={() => toggleActive(p)}
                className={`text-xs mt-2 px-2 py-0.5 rounded-full font-semibold ${p.active ? "bg-light-green text-[#1d6b32]" : "bg-red-100 text-red-700"}`}
                data-testid={`admin-poster-toggle-${i}`}
              >
                {p.active ? "Active — shown to parents" : "Hidden"}
              </button>
            </div>
            <button onClick={() => del(p.id)} className="text-red-600 hover:bg-red-50 p-2 rounded-full" data-testid={`admin-poster-delete-${i}`}><Trash2 size={16} /></button>
          </div>
        ))}
        {posters.length === 0 && <div className="text-sm text-[color:var(--text-secondary)] text-center py-10">No calendar images yet — upload your first.</div>}
      </div>
    </PanelCard>
  );
};

const EventForm = ({ value, setValue, onSubmit, testIdPrefix, submitLabel = "Save event" }) => (
  <form onSubmit={onSubmit} className="mb-2 bg-white rounded-2xl p-5 space-y-3 border border-[#eee]" data-testid={`${testIdPrefix}-form`}>
    <div className="grid sm:grid-cols-3 gap-3">
      <input required placeholder="Title" value={value.title} onChange={(e) => setValue({ ...value, title: e.target.value })} className="bg-cream border border-[#e6e2d8] rounded-xl px-3 py-2 text-sm sm:col-span-2 focus:outline-none focus:border-brand-blue" data-testid={`${testIdPrefix}-title`} />
      <input required type="date" value={value.date} onChange={(e) => setValue({ ...value, date: e.target.value })} className="bg-cream border border-[#e6e2d8] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-brand-blue" data-testid={`${testIdPrefix}-date`} />
    </div>
    <textarea placeholder="Description" value={value.description} onChange={(e) => setValue({ ...value, description: e.target.value })} className="w-full bg-cream border border-[#e6e2d8] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-brand-blue" rows={2} data-testid={`${testIdPrefix}-description`} />
    <select value={value.event_type} onChange={(e) => setValue({ ...value, event_type: e.target.value })} className="bg-cream border border-[#e6e2d8] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-brand-blue" data-testid={`${testIdPrefix}-type`}>
      <option value="event">Event</option><option value="holiday">Holiday</option><option value="celebration">Celebration</option><option value="activity">Activity</option>
    </select>
    <button type="submit" className="btn-primary !py-2 !px-4 !text-sm" data-testid={`${testIdPrefix}-save`}>{submitLabel}</button>
  </form>
);

// =================== ANNOUNCEMENTS ===================
const AnnouncementsManager = () => {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const blank = { title: "", body: "", active: true };
  const [form, setForm] = useState(blank);
  const [editForm, setEditForm] = useState(blank);

  const load = async () => setItems((await api.get("/announcements")).data);
  useEffect(() => { load(); }, []);

  const add = async (e) => { e.preventDefault(); await api.post("/announcements", form); setForm(blank); setAdding(false); load(); };
  const startEdit = (a) => { setEditId(a.id); setEditForm({ title: a.title, body: a.body, active: a.active }); };
  const saveEdit = async () => { await api.patch(`/announcements/${editId}`, editForm); setEditId(null); load(); };
  const del = async (id) => { if (window.confirm("Delete this announcement?")) { await api.delete(`/announcements/${id}`); load(); } };
  const toggleActive = async (a) => { await api.patch(`/announcements/${a.id}`, { active: !a.active }); load(); };

  return (
    <PanelCard
      title="Announcements"
      action={<button onClick={() => setAdding((v) => !v)} className="btn-primary !py-2 !px-4 !text-sm" data-testid="admin-add-announcement-btn"><Plus size={16} /> {adding ? "Cancel" : "Add"}</button>}
    >
      <p className="text-xs text-[color:var(--text-secondary)] mb-3">The most recent active announcement appears as a banner on the public homepage.</p>
      {adding && <AnnouncementForm value={form} setValue={setForm} onSubmit={add} testIdPrefix="admin-announcement" />}

      <div className="space-y-3">
        {items.map((a, i) => (
          <div key={a.id} className="bg-cream rounded-2xl p-4" data-testid={`admin-announcement-item-${i}`}>
            {editId === a.id ? (
              <div className="space-y-2">
                <AnnouncementForm value={editForm} setValue={setEditForm} onSubmit={(e) => { e.preventDefault(); saveEdit(); }} testIdPrefix={`admin-announcement-edit-${i}`} submitLabel="Save changes" />
                <button onClick={() => setEditId(null)} className="text-xs text-[color:var(--text-secondary)] underline" data-testid={`admin-announcement-edit-cancel-${i}`}>Cancel edit</button>
              </div>
            ) : (
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-heading font-bold">{a.title}</div>
                    <button onClick={() => toggleActive(a)} className={`text-xs px-2 py-0.5 rounded-full font-semibold ${a.active ? "bg-light-green text-[#1d6b32]" : "bg-red-100 text-red-700"}`} data-testid={`admin-announcement-toggle-${i}`}>
                      {a.active ? "Active" : "Hidden"}
                    </button>
                  </div>
                  <p className="text-sm text-[color:var(--text-secondary)] mt-1">{a.body}</p>
                </div>
                <button onClick={() => startEdit(a)} className="text-brand-blue hover:bg-light-blue p-2 rounded-full" data-testid={`admin-announcement-edit-${i}`}><Edit3 size={16} /></button>
                <button onClick={() => del(a.id)} className="text-red-600 hover:bg-red-50 p-2 rounded-full" data-testid={`admin-announcement-delete-${i}`}><Trash2 size={16} /></button>
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-[color:var(--text-secondary)] text-center py-10">No announcements yet.</div>}
      </div>
    </PanelCard>
  );
};

const AnnouncementForm = ({ value, setValue, onSubmit, testIdPrefix, submitLabel = "Save" }) => (
  <form onSubmit={onSubmit} className="mb-2 bg-white rounded-2xl p-5 space-y-3 border border-[#eee]" data-testid={`${testIdPrefix}-form`}>
    <input required placeholder="Title" value={value.title} onChange={(e) => setValue({ ...value, title: e.target.value })} className="w-full bg-cream border border-[#e6e2d8] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-brand-blue" data-testid={`${testIdPrefix}-title`} />
    <textarea required placeholder="Body" value={value.body} onChange={(e) => setValue({ ...value, body: e.target.value })} className="w-full bg-cream border border-[#e6e2d8] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-brand-blue" rows={3} data-testid={`${testIdPrefix}-body`} />
    <label className="text-sm flex items-center gap-2">
      <input type="checkbox" checked={value.active} onChange={(e) => setValue({ ...value, active: e.target.checked })} data-testid={`${testIdPrefix}-active`} /> Active (show as banner)
    </label>
    <button type="submit" className="btn-primary !py-2 !px-4 !text-sm" data-testid={`${testIdPrefix}-save`}>{submitLabel}</button>
  </form>
);

// =================== SUBMISSIONS ===================
const SubmissionsManager = () => {
  const [items, setItems] = useState([]);
  const load = async () => setItems((await api.get("/contact")).data);
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (window.confirm("Delete this submission?")) { await api.delete(`/contact/${id}`); load(); } };

  return (
    <PanelCard title={`Inbox (${items.length})`}>
      <div className="space-y-3">
        {items.map((s, i) => (
          <details key={s.id} className="bg-cream rounded-2xl p-4 group" data-testid={`admin-submission-item-${i}`}>
            <summary className="cursor-pointer flex items-center gap-3 list-none">
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold">{s.name} <span className="text-xs text-[color:var(--text-secondary)] font-normal">• {s.subject}</span></div>
                <div className="text-xs text-[color:var(--text-secondary)]">{s.email}{s.phone ? ` • ${s.phone}` : ""}</div>
              </div>
              <div className="text-xs text-[color:var(--text-secondary)]">{new Date(s.created_at).toLocaleDateString()}</div>
              <button onClick={(e) => { e.preventDefault(); del(s.id); }} className="text-red-600 hover:bg-red-50 p-2 rounded-full" data-testid={`admin-submission-delete-${i}`}><Trash2 size={16} /></button>
            </summary>
            <p className="text-sm text-[color:var(--text-secondary)] mt-3 whitespace-pre-wrap">{s.message}</p>
          </details>
        ))}
        {items.length === 0 && <div className="text-sm text-[color:var(--text-secondary)] text-center py-10">No submissions yet.</div>}
      </div>
    </PanelCard>
  );
};

// =================== VISITS ===================
const VisitsManager = () => {
  const [items, setItems] = useState([]);
  const load = async () => setItems((await api.get("/visits")).data);
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (window.confirm("Delete this visit request?")) { await api.delete(`/visits/${id}`); load(); } };
  const setStatus = async (id, status) => { await api.patch(`/visits/${id}`, { status }); load(); };

  const statusColors = {
    pending: "bg-soft-orange text-[color:var(--text-primary)]",
    confirmed: "bg-light-green text-[#1d6b32]",
    done: "bg-light-blue text-brand-blue",
  };

  return (
    <PanelCard title={`School Visit Requests (${items.length})`}>
      <div className="space-y-3">
        {items.map((v, i) => (
          <div key={v.id} className="bg-cream rounded-2xl p-4" data-testid={`admin-visit-item-${i}`}>
            <div className="flex items-start gap-4 flex-wrap">
              <div className="w-14 h-14 rounded-xl bg-brand-yellow text-[color:var(--text-primary)] flex flex-col items-center justify-center flex-shrink-0">
                <div className="text-[0.65rem] uppercase font-bold">{new Date(v.preferred_date).toLocaleString("en", { month: "short" })}</div>
                <div className="font-heading text-lg font-bold leading-none">{new Date(v.preferred_date).getDate()}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-heading font-bold">{v.parent_name}</div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold capitalize ${statusColors[v.status] || statusColors.pending}`}>{v.status}</span>
                  <span className="text-xs text-[color:var(--text-secondary)]">• Child: {v.child_name} ({v.child_age})</span>
                </div>
                <div className="text-sm text-[color:var(--text-secondary)] mt-1">
                  <span className="font-semibold text-[color:var(--text-primary)]">{v.preferred_time}</span> · {v.email}{v.phone ? ` · ${v.phone}` : ""}
                </div>
                {v.notes && <p className="text-sm text-[color:var(--text-secondary)] mt-1 italic">"{v.notes}"</p>}
              </div>
              <div className="flex gap-1 flex-wrap">
                {v.status !== "confirmed" && (
                  <button onClick={() => setStatus(v.id, "confirmed")} className="text-xs px-3 py-1 rounded-full bg-light-green text-[#1d6b32] font-semibold hover:bg-green-200" data-testid={`admin-visit-confirm-${i}`}>Confirm</button>
                )}
                {v.status !== "done" && (
                  <button onClick={() => setStatus(v.id, "done")} className="text-xs px-3 py-1 rounded-full bg-light-blue text-brand-blue font-semibold hover:bg-blue-200" data-testid={`admin-visit-done-${i}`}>Mark done</button>
                )}
                <button onClick={() => del(v.id)} className="text-red-600 hover:bg-red-50 p-1.5 rounded-full" data-testid={`admin-visit-delete-${i}`}><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-[color:var(--text-secondary)] text-center py-10">No school visit requests yet.</div>}
      </div>
    </PanelCard>
  );
};

export default AdminDashboard;
